/*
Page "Statistiques" — réservée aux admins. Tableau par utilisateur :
dernière connexion, dernier envoi, nombre total de cartes, et une
mini-courbe d'usage des 90 derniers jours rendue en SVG inline.
*/

import { useEffect, useState, type JSX } from "react";

import { ApiError, api } from "../lib/api";

interface UserStats
{
	id:				string;
	email:			string;
	role:			"USER" | "ADMIN";
	disabledAt:		number | null;
	lastLoginAt:	number | null;
	lastUploadAt:	number | null;
	cardCount:		number;
	uploadsByDay:	number[];	// Aligné sur la propriété `days`, oldest first
}

interface StatsResponse
{
	windowDays:	number;
	days:		string[];	// "YYYY-MM-DD", oldest first
	users:		UserStats[];
}

export function StatsPage(): JSX.Element
{
	const [fStats, fSetStats]	= useState<StatsResponse | null>(null);
	const [fError, fSetError]	= useState<string | null>(null);

	useEffect(() =>
	{
		api<StatsResponse>("GET", "/admin/stats")
			.then(fSetStats)
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : "Erreur réseau.");
			});
	}, []);

	if (fError !== null)
	{
		return <div className="card error">Erreur de chargement : {fError}</div>;
	}

	if (fStats === null)
	{
		return <div className="muted">Chargement…</div>;
	}

	if (fStats.users.length === 0)
	{
		return (
			<div className="card">
				<h2>Statistiques</h2>
				<p className="muted">Aucun utilisateur enregistré.</p>
			</div>
		);
	}

	return (
		<div className="card">
			<h2>Statistiques ({fStats.users.length} utilisateur{fStats.users.length > 1 ? "s" : ""})</h2>
			<p className="muted">
				Activité sur les {fStats.windowDays} derniers jours. Chaque
				barre représente le nombre de cartes téléversées un jour donné.
			</p>
			<table>
				<thead>
					<tr>
						<th>Utilisateur</th>
						<th>Dernière connexion</th>
						<th>Dernier envoi</th>
						<th>Cartes</th>
						<th>Activité {fStats.windowDays} j</th>
					</tr>
				</thead>
				<tbody>
					{fStats.users.map((u) => (
						<tr key={u.id}>
							<td>
								{u.email}
								{u.role === "ADMIN" ? <span className="tag tag--admin" style={{ marginLeft: "0.4rem" }}>admin</span> : null}
								{u.disabledAt !== null ? <span className="tag tag--disabled" style={{ marginLeft: "0.4rem" }}>désactivé</span> : null}
							</td>
							<td>{formatTimestamp(u.lastLoginAt)}</td>
							<td>{formatTimestamp(u.lastUploadAt)}</td>
							<td style={{ fontVariantNumeric: "tabular-nums" }}>{u.cardCount}</td>
							<td>
								<Sparkline
									values={u.uploadsByDay}
									dayLabels={fStats.days}
								/>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/*
Mini-bar-chart en SVG, sans dépendance. Hauteur des barres
proportionnelle au max de la série (zéro = barre invisible). Un title
SVG sur chaque barre fournit la date + le nombre exact au survol.
*/
function Sparkline(
	props:	{ values: number[]; dayLabels: string[] }
): JSX.Element
{
	const vMax		= Math.max(1, ...props.values);	// Hauteur max relative (jamais 0 → pas de div par 0)
	const vBarW		= 4;								// Largeur d'une barre
	const vGap		= 1;								// Espace entre deux barres
	const vWidth	= props.values.length * (vBarW + vGap) - vGap;
	const vHeight	= 28;

	return (
		<svg
			width={vWidth}
			height={vHeight}
			viewBox={`0 0 ${vWidth} ${vHeight}`}
			role="img"
			aria-label="Activité des 90 derniers jours"
		>
			{props.values.map((vCount, vIdx) =>
			{
				const vH = (vCount / vMax) * vHeight;	// Hauteur proportionnelle
				const vX = vIdx * (vBarW + vGap);

				return (
					<rect
						key={vIdx}
						x={vX}
						y={vHeight - vH}
						width={vBarW}
						height={vH}
						fill="var(--color-primary)"
					>
						<title>{`${props.dayLabels[vIdx]} : ${vCount}`}</title>
					</rect>
				);
			})}
		</svg>
	);
}

/*
Format "YYYY-MM-DD HH:MM" en local, ou "—" pour null.
*/
function formatTimestamp(
	inMs:	number | null
): string
{
	if (inMs === null)
	{
		return "—";
	}

	const vDate	= new Date(inMs);
	const pad	= (n: number) => String(n).padStart(2, "0");

	return `${vDate.getFullYear()}-${pad(vDate.getMonth() + 1)}-${pad(vDate.getDate())} ${pad(vDate.getHours())}:${pad(vDate.getMinutes())}`;
}
